"use client";

import { User, UserStatusCode } from "../services/driver-service";
import ImageCustom from "../../../components/custom/image-custom";

interface UserDetailModalProps {
  userName: string;
  data: User;
  loading: boolean;
  onClose: () => void;
}

const DriverDetailModal: React.FC<UserDetailModalProps> = ({
  userName,
  data,
  loading,
  onClose,
}) => {
  const statusLabel =
    data.status === UserStatusCode.ACTIVE ? "Đang hoạt động" : "Tạm ngưng";
  const statusColor =
    data.status === UserStatusCode.ACTIVE ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm px-4">
      <div className="relative w-full max-w-4xl rounded-2xl bg-white shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between border-b px-6 py-4 bg-gradient-to-r from-indigo-50 to-blue-50">
          <h3 className="text-xl font-semibold text-slate-800">
            👤 Chi tiết Tài xế
          </h3>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 text-2xl leading-none"
          >
            ✕
          </button>
        </div>

        {loading ? (
          <div className="p-8 text-center text-slate-500 text-sm">
            Đang tải thông tin người dùng...
          </div>
        ) : (
          <div className="p-6 space-y-6">
            {/* Avatar + Basic Info */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-6">
              <div className="flex-shrink-0">
                <ImageCustom
                  url={data.avatar || undefined}
                  isLocal={false}
                  isUser={true}
                  className="h-32 w-32 rounded-full border-4 border-indigo-100 object-cover shadow-md"
                />
              </div>
              <div className="flex-1 space-y-2">
                <h2 className="text-2xl font-bold text-slate-800">
                  {data.name || userName}
                </h2>
                <p className="text-slate-500">@{data.username}</p>
                <span
                  className={`inline-block mt-2 rounded-full px-3 py-1 text-sm font-medium ${statusColor}`}
                >
                  {statusLabel}
                </span>
              </div>
            </div>

            {/* Info Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4 text-sm">
              {/* === Thông tin cơ bản === */}
              <div>
                <h4 className="font-semibold text-slate-700 mb-2 border-l-4 border-indigo-400 pl-2">
                  🧾 Thông tin cơ bản
                </h4>
                <ul className="space-y-1 text-slate-600">
                  <li>
                    <b>ID:</b> {data.id}
                  </li>
                  <li>
                    <b>Phòng ban:</b> {data.department_name || "—"}
                  </li>
                  <li>
                    <b>Ngày sinh:</b> {data.birthday || "—"}
                  </li>
                  <li>
                    <b>Ngày làm việc:</b> {data.joining_date || "—"}
                  </li>
                </ul>
              </div>

              {/* === Liên hệ & phân quyền === */}
              <div>
                <h4 className="font-semibold text-slate-700 mb-2 border-l-4 border-teal-400 pl-2">
                  📞 Liên hệ & phân quyền
                </h4>
                <ul className="space-y-1 text-slate-600">
                  <li>
                    <b>Email:</b> {data.email || "—"}
                  </li>
                  <li>
                    <b>Điện thoại:</b> {data.phone || "—"}
                  </li>
                  <li>
                    <b>Địa chỉ:</b> {data.address || "—"}
                  </li>
                  <li>
                    <b>Nhóm quyền:</b> {data.privilege_group_name || "—"}
                  </li>
                </ul>
              </div>

              {/* === Chi nhánh / Đại lý === */}
              <div className="md:col-span-2">
                <h4 className="font-semibold text-slate-700 mb-2 border-l-4 border-orange-400 pl-2">
                  🏢 Chi nhánh áp dụng
                </h4>
                {data.is_all_branch === 1 ? (
                  <p className="text-green-600 text-sm font-medium">
                    ✅ Tất cả đại lý
                  </p>
                ) : Array.isArray(data.branches) && data.branches.length > 0 ? (
                  <ul className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {data.branches.map((b: any) => (
                      <li
                        key={b.id}
                        className="rounded-lg bg-slate-50 px-3 py-2 border text-slate-600 text-sm"
                      >
                        {b.name || `Chi nhánh #${b.id}`}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-slate-500 text-sm">Không có chi nhánh nào</p>
                )}
              </div>

              {/* === Thông tin hệ thống === */}
              <div className="md:col-span-2">
                <h4 className="font-semibold text-slate-700 mb-2 border-l-4 border-purple-400 pl-2">
                  ⚙️ Thông tin hệ thống
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-1 text-slate-600 text-sm">
                  <div>
                    <b>Tạo lúc:</b> {data.created_at}
                  </div>
                  <div>
                    <b>Cập nhật:</b> {data.updated_at}
                  </div>
                  <div>
                    <b>Mã công ty:</b> {data.company_id || data.log_company_id}
                  </div>
                  <div>
                    <b>2FA:</b> {data.is_2fa === 1 ? "Bật" : "Tắt"}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="border-t bg-slate-50 px-6 py-4 flex justify-end">
          <button
            onClick={onClose}
            className="rounded-full border border-slate-300 bg-white px-5 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 transition"
          >
            Đóng
          </button>
        </div>
      </div>
    </div>
  );
};

export default DriverDetailModal;
